import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';

import { noop } from '../lib/serviceHelpers';

export const useFetchDashboardClaims = (params, opts = {}) => {
  return useQuery(
    ['claim', params],
    async () => {
      const res = await axios.get(`/dashboard/claims`, { params });
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
      ...opts,
    },
  );
};

// opts: e.g. the client portal's refresh (ClientDashboard).
export const useFetchClaim = (claimId, opts = {}) => {
  return useQuery(
    ['claim', claimId],
    async () => {
      const res = await axios.get(`/claims/${claimId}?user=1`);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
      ...opts,
    },
  );
};

export const useFetchClaimProfile = (claimId) => {
  return useQuery(
    ['claim-profile', claimId],
    async () => {
      const res = await axios.get(`/claims/${claimId}/profile`);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
      // 404 / 403 are inherent to the resource (provider lost access, claim
      // deleted, etc.) — retrying just makes the user wait ~3s before the
      // error UI in Profile.js can react. Bail out immediately on those.
      retry: (failureCount, err) => {
        const status = err?.response?.status;
        if (status === 404 || status === 403) return false;
        return failureCount < 3;
      },
    },
  );
};

export const useFetchCurrentClaim = () => {
  return useQuery(
    ['current-claim'],
    async () => {
      try {
        const res = await axios.get(`/claims/current`);
        return res.data;
      } catch (_) {
        return null;
      }
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
    },
  );
};

export const useFetchQuestionnaireFields = (opts = {}) => {
  return useQuery(
    ['questionnaire'],
    async () => {
      const res = await axios.get(`/claims/questionnaire`);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 500000,
      ...opts,
    },
  );
};

export const useUpdateClaimWithUser = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, payload }) => {
      const res = await axios.put(`/claims/${claimId}/with-user`, {
        ...payload,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useUpdateClaim = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, payload }) => {
      const res = await axios.put(`/claims/${claimId}`, {
        ...payload,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useDuplicateClaim = (callback, opts = {}) => {
  return useMutation(
    async (claimId) => {
      const res = await axios.put(`/claims/${claimId}/duplicate`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useSubmitQuestionnaire = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, payload }) => {
      const res = await axios.put(`/claims/${claimId}/questionnaire`, {
        ...payload,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useDeleteClaim = (callback, opts = {}) => {
  return useMutation(
    async (claimId) => {
      const res = await axios.delete(`/claims/${claimId}`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useDeleteClaimFile = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, fileId }) => {
      const res = await axios.delete(`/claims/${claimId}/files/${fileId}`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useGetPresignedClaimFile = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, fileId }) => {
      const res = await axios.get(`/claims/${claimId}/files/${fileId}/read`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useUpdateTranscript = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, transcriptId, payload }) => {
      const res = await axios.put(
        `/claims/${claimId}/profile/transcripts/${transcriptId}`,
        {
          ...payload,
        },
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useReprocessAddon = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, addonId }) => {
      const res = await axios.put(
        `/claims/${claimId}/profile/addons/${addonId}/reprocess`,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useToggleClaimTask = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, taskId }) => {
      const res = await axios.put(
        `/claims/${claimId}/profile/tasks/${taskId}/toggle`,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useSetClaimAssignment = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, body }) => {
      const res = await axios.put(
        `/claims/${claimId}/profile/assignments`,
        body,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useConfirmClaimRating = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, body }) => {
      const res = await axios.post(
        `/claims/${claimId}/profile/confirm-rating`,
        body,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useRequestClaimDiscount = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, body }) => {
      const res = await axios.post(
        `/claims/${claimId}/profile/discount-requests`,
        body,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useReviewClaimDiscount = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, requestId, body }) => {
      const res = await axios.patch(
        `/claims/${claimId}/profile/discount-requests/${requestId}`,
        body,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useToggleFileLock = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, fileId }) => {
      const res = await axios.put(
        `/claims/${claimId}/files/${fileId}/toggle-lock`,
      );
      return res.data;
    },
    {
      ...opts,
      onSuccess: callback || noop,
      onError: (err) => toast.error(err.message),
    },
  );
};

export const useRenameClaimFile = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, fileId, fileName }) => {
      const res = await axios.put(`/claims/${claimId}/files/${fileId}`, {
        fileName,
      });
      return res.data;
    },
    {
      ...opts,
      onSuccess: callback || noop,
      onError: (err) => toast.error(err.response?.data?.msg || err.message),
    },
  );
};

export const useEditClaimFile = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, fileId, fileName, fileDir }) => {
      const payload = {};
      if (fileName !== undefined) payload.fileName = fileName;
      if (fileDir !== undefined) payload.fileDir = fileDir;

      const res = await axios.put(`/claims/${claimId}/files/${fileId}`, payload);
      return res.data;
    },
    {
      ...opts,
      onSuccess: callback || noop,
      onError: (err) => toast.error(err.response?.data?.msg || err.message),
    },
  );
};

export const useFetchParsedContent = (claimId, fileId, opts = {}) => {
  return useQuery(
    ['parsed-content', claimId, fileId],
    async () => {
      const res = await axios.get(
        `/casegpt/claims/${claimId}/files/${fileId}/content`,
      );
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60000,
      ...opts,
    },
  );
};

export const downloadAllClaimFiles = async (claimId, token) => {
  const url = `${process.env.REACT_APP_API_URL}/claims/${claimId}/files/download-all?jwt=${token}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      toast.error(errorData.msg || 'Failed to download files');
      return;
    }

    // Get the blob from the response
    const blob = await response.blob();

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `claim-${claimId}-files.zip`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Create a blob URL and trigger download
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL
    window.URL.revokeObjectURL(blobUrl);

    toast.success('Files downloaded successfully');
  } catch (err) {
    console.error('Download error:', err);
    toast.error('Failed to download files');
  }
};
